import { makeStyles, tokens, mergeClasses } from '@fluentui/react-components';
import { SavedView } from './types';
import i18n from '../../i18n';

// Unified list-filters program (spec 2026-07-22) — shared quick-view pills, re-styled in Fluent
// `makeStyles` for supervision (CRM's source is Tailwind). Same BEHAVIOUR everywhere: a row of
// one-click presets that select a saved view. Deliberately plain <button>s, NOT a controlled
// Fluent Dropdown — so the aria-label/title render-loop hazard can't bite here.

const useStyles = makeStyles({
  group: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    flexWrap: 'wrap',
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: tokens.borderRadiusCircular,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    cursor: 'pointer',
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground2,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ':focus-visible': {
      outline: `${tokens.strokeWidthThick} solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '1px',
    },
  },
  active: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorBrandBackground}`,
    ':hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
      color: tokens.colorNeutralForegroundOnBrand,
    },
  },
});

export interface QuickViewPillsProps {
  /**
   * Already-resolved views to surface as pills, in display order. Resolution of pinned keys
   * against the page's view list (dropping unknown keys) is the caller's job — this component is
   * generic over the view list and owns no state.
   */
  views: SavedView[];
  /** Active view key — the matching pill is filled. */
  activeKey: string;
  /** Selecting a pill drives the caller's view-selection path. */
  onSelect: (key: string) => void;
}

export function QuickViewPills({ views, activeKey, onSelect }: QuickViewPillsProps) {
  const styles = useStyles();
  if (views.length === 0) return null;
  return (
    <div role="group" aria-label={i18n.t('filters.quickViews')} className={styles.group}>
      {views.map((v) => {
        const active = v.key === activeKey;
        return (
          <button
            key={v.key}
            type="button"
            aria-pressed={active}
            onClick={() => onSelect(v.key)}
            className={mergeClasses(styles.pill, active && styles.active)}
          >
            {v.label}
          </button>
        );
      })}
    </div>
  );
}
